import Image from 'next/future/image';
import useSWR from 'swr';
import Link from 'next/link';
import Loading from '@/components/Loading/Loading';
import cn from 'classnames';
import { Breakpoints } from '@/settings/breakpoints';

import useCountdownTimer from '@/lib/use-countdown-timer';
import { TopDealProduct } from '@/lib/hot-deals/helpers';

import { calculateSavings, getOfferUri } from '@/lib/offer';
import { priceFormater } from '@/settings/index';

import Section from '@/components/commons/Section';
import Slider from '@/components/slider/Slider';
import Themes from './Themes';

export const thankYouPageBreakpoints = {
  [Breakpoints.NONE]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.XS]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.SM]: {
    slidesPerView: 2.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.LG]: {
    slidesPerView: 2,
    slidesPerGroup: 3,
    spaceBetween: 15,
  },
};
export const breakpoints = {
  [Breakpoints.NONE]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.XS]: {
    slidesPerView: 1.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.SM]: {
    slidesPerView: 2.5,
    slidesPerGroup: 1,
    spaceBetween: 15,
  },
  [Breakpoints.LG]: {
    slidesPerView: 3,
    slidesPerGroup: 3,
    spaceBetween: 15,
  },
};

const Deals = ({
  isThankYouPage = false,
  title = "Today's Top Deals",
}: {
  isThankYouPage?: boolean;
  title?: string;
}) => {
  const countdown = useCountdownTimer();
  const { data: deals } = useSWR<TopDealProduct[]>(() => `/api/topdeals`);

  if (!deals) {
    return (
      <div className="mb-10 py-5">
        <Loading />
      </div>
    );
  }

  if (deals.length > 0) {
    return (
      <div className="bg-white">
        <Section className={cn({ 'mt-10': isThankYouPage })}>
          <div
            className={cn({
              'reebelo-container': !isThankYouPage,
            })}
          >
            <h2 className="flex items-center text-lg font-semibold md:text-xl">
              {title}
            </h2>
            <div className="mt-6 flex flex-col lg:flex-row">
              <Themes title="Today Deals" countdown={countdown} />
              <div className="relative rounded-b-2xl bg-gray-200 px-4 lg:w-2/3 lg:rounded-l-none lg:rounded-r-2xl lg:py-0 lg:pr-0">
                <div className=" w-full">
                  <Slider
                    breakpoints={
                      isThankYouPage ? thankYouPageBreakpoints : breakpoints
                    }
                    speed={300}
                    className="!py-4 !pr-4 lg:!pl-0"
                  >
                    {deals.map((deal, index) => {
                      const savingsPercentage = calculateSavings(
                        deal.price,
                        deal.comparePrice,
                      );

                      return (
                        <Link
                          href={getOfferUri(deal as any)}
                          passHref
                          key={deal.gid}
                        >
                          <div className="flex h-full w-full cursor-pointer flex-col rounded-xl bg-white p-4">
                            <div className="absolute -right-3 -top-3 flex h-9 w-9 items-center justify-center rounded-full border-4 border-gray-200 bg-gray-700 text-xxs  text-white">
                              #
                              <span className="ml-[1px] font-extrabold">
                                {index + 1}
                              </span>
                            </div>
                            <div className="mt-2">
                              <div className="flex items-center justify-center">
                                <div className="h-24 w-24">
                                  <Image
                                    width={100}
                                    height={100}
                                    alt={deal.title}
                                    src={deal.imageUrl}
                                    className="h-full w-full object-contain"
                                  />
                                </div>
                              </div>

                              <div className="mt-5 flex h-6 items-center gap-2 text-xs">
                                {savingsPercentage > 3 && (
                                  <>
                                    <div className="flex h-6 items-center justify-center rounded-sm bg-red px-2 text-[11px] font-extrabold text-white">
                                      {savingsPercentage}% off
                                    </div>
                                    <span className="hidden font-extrabold text-red xxs:block">
                                      Deal
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="mt-3 flex flex-1 flex-col justify-between">
                              <h3
                                // eslint-disable-next-line tailwindcss/no-custom-classname
                                className="two-line-ellipsis text-sm font-light"
                              >
                                {deal.title}
                              </h3>
                              <h4 className="mt-5 text-sm font-extrabold">
                                {priceFormater.long(deal.price)}
                              </h4>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </Slider>
                </div>
              </div>
            </div>
          </div>
        </Section>
      </div>
    );
  }

  return null;
};

export default Deals;
